// Pathways page content. Every figure is from the shipped content corpus
// (verified 11 June 2026). House rules: no em-dashes, estimates are labeled
// estimates, and ineligible routes stay visible with the reason.

// The interactive match specimen uses two real destinations and their real
// routes, with the actual eligibility rules from the product. Times, costs,
// exam counts, outcomes, and eligibility notes are from the route corpus.

export const trainingCountryOptions = [
  'United States',
  'United Kingdom',
  'Ireland',
  'Canada',
  'New Zealand',
  'India',
  'Nigeria',
  'Pakistan',
  'Egypt',
  'Other / not listed',
];

const CA_COUNTRIES = ['United Kingdom', 'Ireland', 'United States', 'Canada', 'New Zealand'];

export const matchDestinations = [
  {
    id: 'canada',
    flag: '\ud83c\udde8\ud83c\udde6',
    name: 'Canada',
    routes: [
      {
        id: 'fasttrack',
        name: 'US board-certified fast track',
        tagline: 'ABMS/ABFM certification recognized directly',
        time: '3\u20139 mo',
        timeLow: 3,
        cost: '$2,000\u2013$5,000',
        exams: 0,
        outcome: 'Full or restricted-independent provincial licence',
        notes: 'Full licensure immediately in 7 provinces; restricted-independent in ON/NL',
        eligible: (p) => p.country === 'United States' && p.specialist,
      },
      {
        id: 'pra',
        name: 'Practice-Ready Assessment',
        tagline: 'Supervised assessment instead of repeating residency',
        time: '12\u201324 mo',
        timeLow: 12,
        cost: '$6,000\u2013$12,000',
        exams: 1,
        outcome: 'Provisional to full provincial licence with return of service',
        notes: '2+ years postgraduate training; mostly family medicine; return-of-service terms',
        eligible: (p) => p.years === '2+' && !p.specialist,
      },
      {
        id: 'carms',
        name: 'Canadian residency (CaRMS)',
        tagline: 'Exams and the residency match: the standard route',
        time: '36\u201372 mo',
        timeLow: 36,
        cost: '$7,000\u2013$14,000',
        exams: 2,
        outcome: 'Provincial licence after residency and certification',
        notes: 'Designed for early-career IMGs; IMG seats are competitive',
        eligible: (p) => p.years === '0-1',
      },
    ],
  },
  {
    id: 'australia',
    flag: '\ud83c\udde6\ud83c\uddfa',
    name: 'Australia',
    routes: [
      {
        id: 'competent',
        name: 'Competent Authority pathway',
        tagline: 'No AMC exams for UK, Ireland, US, Canada or NZ-trained doctors',
        time: '6\u201318 mo',
        timeLow: 6,
        cost: '$3,000\u2013$6,000',
        exams: 0,
        outcome: 'General registration after 12 months supervised practice',
        notes: 'Qualifying exam and supervised experience completed in a Competent Authority country',
        eligible: (p) => CA_COUNTRIES.includes(p.country) && !p.specialist,
      },
      {
        id: 'standard',
        name: 'Standard pathway (AMC exams)',
        tagline: 'MCQ and clinical exam to provisional registration',
        time: '18\u201336 mo',
        timeLow: 18,
        cost: '$8,000\u2013$14,000',
        exams: 2,
        outcome: 'General registration after supervised practice',
        notes: 'For graduates outside Competent Authority countries',
        eligible: (p) => !CA_COUNTRIES.includes(p.country) && !p.specialist,
      },
      {
        id: 'specialist',
        name: 'Specialist pathway',
        tagline: 'College comparability assessment',
        time: '12\u201336 mo',
        timeLow: 12,
        cost: '$8,000\u2013$20,000',
        exams: 0,
        outcome: 'Specialist registration and fellowship',
        notes: 'Recognized specialist qualification required',
        eligible: (p) => p.specialist,
      },
    ],
  },
];

export const corpusStats = [
  { value: '8', label: 'Destination countries with published routes' },
  { value: '15', label: 'Explicitly modeled routes, each a versioned object with an eligibility rule' },
  { value: '81', label: 'Concrete steps, 100% carrying official source citations' },
  { value: '44', label: 'Official domains cited, 95 citations in total: the regulators themselves' },
];

export const corpusFootnote =
  'Counted directly from the shipped content, verified 11 June 2026. Time and cost figures are route-level estimates and exclude living costs, travel, relocation, and most courier and translation fees. They say so in the product too.';

export const howItWorks = [
  {
    name: 'Compare',
    text: 'Pick where you trained and where you want to practise, answer four questions, and every route to that destination appears side by side: time, estimated cost, exam steps, and the licence you end up holding. Routes you do not qualify for stay on screen, greyed, with the reason. Nothing is hidden.',
  },
  {
    name: 'Commit',
    text: 'Choose a route and the platform freezes a personalized, dependency-ordered plan: only the steps that apply to you, in an order that respects what must happen before what. Your plan never silently changes under you; if the underlying route is updated, a banner tells you, and you choose when to look.',
  },
  {
    name: 'Execute',
    text: 'Each step becomes a small workspace: official source links with verified-on dates, estimated fee and elapsed weeks, status, to-dos, dated deadlines, notes, and the documents that step needs, ticked off automatically against your vault.',
  },
];

export const stages = [
  { name: 'Eligibility', count: 8 },
  { name: 'Language', count: 6 },
  { name: 'Exams', count: 16 },
  { name: 'Verification', count: 5 },
  { name: 'Training and jobs', count: 7 },
  { name: 'Registration', count: 31 },
  { name: 'Visa', count: 8 },
];

export const destinations = [
  { flag: '\ud83c\uddfa\ud83c\uddf8', name: 'United States', routes: 2, fastest: '12\u201336 mo', cheapest: '$5,000\u2013$10,000', sub: '54 states and territories modeled; 23 states plus Guam flagged for alternative pathways' },
  { flag: '\ud83c\udde8\ud83c\udde6', name: 'Canada', routes: 3, fastest: '3\u20139 mo', cheapest: '$2,000\u2013$5,000', sub: '10 provinces modeled; Quebec adds a French proficiency step' },
  { flag: '\ud83c\uddec\ud83c\udde7', name: 'United Kingdom', routes: 2, fastest: '12\u201324 mo', cheapest: '$2,500\u2013$6,000', sub: 'PLAB / UKMLA route and the Specialist Portfolio Pathway' },
  { flag: '\ud83c\udde6\ud83c\uddfa', name: 'Australia', routes: 3, fastest: '6\u201318 mo', cheapest: '$3,000\u2013$6,000', sub: 'Competent Authority, Standard, and Specialist pathways' },
  { flag: '\ud83c\uddf3\ud83c\uddff', name: 'New Zealand', routes: 2, fastest: '3\u20139 mo', cheapest: '$2,000\u2013$5,000', sub: '29 comparable-health-system countries skip NZREX' },
  { flag: '\ud83c\udde6\ud83c\uddea', name: 'United Arab Emirates', routes: 1, fastest: '3\u20138 mo', cheapest: '$1,500\u2013$3,500', sub: 'DHA, DOH, and MOHAP modeled as separate authorities' },
  { flag: '\ud83c\uddf8\ud83c\udde6', name: 'Saudi Arabia', routes: 1, fastest: '3\u20139 mo', cheapest: '$1,200\u2013$3,000', sub: 'Mumaris Plus classification and SMLE' },
  { flag: '\ud83c\uddf6\ud83c\udde6', name: 'Qatar', routes: 1, fastest: '3\u20139 mo', cheapest: '$1,200\u2013$3,000', sub: 'DataFlow, qualifying exam, and DHP licence' },
];

export const destinationsNote =
  'Bahrain, Kuwait, and Oman are listed in the product as destinations-in-waiting, visibly marked as having no published routes yet.';

export const vaultDocs = [
  { name: 'Medical licence certificate', steps: 36 },
  { name: 'Certificate of good standing', steps: 31 },
  { name: 'Passport / photo ID', steps: 8 },
  { name: 'CV / r\u00e9sum\u00e9', steps: 7 },
  { name: 'English test result (OET / IELTS)', steps: 6 },
  { name: 'Medical degree / diploma', steps: 5 },
  { name: 'Medical school transcript', steps: 5 },
];

export const honesty = [
  {
    title: 'Ineligibility is a feature',
    text: 'Routes you do not qualify for stay on screen, greyed, with the requirement stated in plain words. Telling somebody early that a route is closed to them, and why, is worth more than any amount of encouragement.',
  },
  {
    title: 'Estimates that say so',
    text: 'Every time and cost figure is labeled a route-level estimate, with the exclusions stated: living costs, travel, relocation, and most courier and translation fees are not included, and the product says exactly that.',
  },
  {
    title: 'A plan that never moves under you',
    text: 'Your journey is a frozen snapshot of the route on the day you committed, with its version number recorded. If the route is updated later, you see a banner telling you so, and you choose when to review the change.',
  },
  {
    title: 'Sourced to the regulator, with dates',
    text: 'All 81 steps carry citations to official domains, each with a verified-on date: the GMC, ECFMG, the Medical Council of Canada, AHPRA, SCFHS, DataFlow, state and provincial boards. A citable reference, not an opinion.',
  },
];

export const faq = [
  {
    q: 'Is this immigration or legal advice?',
    a: 'No. Pathways is a reference product: explicit, versioned routes sourced to the regulators\u2019 own published requirements, each step with official links and a verified-on date. For legal questions about your individual case, you want a lawyer; for what the regulator publishes and what order it happens in, you want this.',
  },
  {
    q: 'What happens when a regulator changes the rules?',
    a: 'Routes are versioned objects. Your committed plan stays frozen exactly as you started it, and a banner appears telling you the underlying route has been updated. You review the change on your terms; nothing is silently rewritten.',
  },
  {
    q: 'How accurate are the costs?',
    a: 'They are estimates, and they are labeled as estimates. Ranges cover typical regulator and exam fees for the route, and exclude living costs, travel, relocation, and most courier and translation fees. Eighteen steps carry named fees drawn from the official fee schedules.',
  },
  {
    q: 'Can I compare two routes by actually starting both?',
    a: 'Yes. You can run as many journeys as you like, including two different routes to the same country, each with its own independent progress. The document vault spans all of them: upload a document once and every step that needs it, in every journey, ticks it off.',
  },
  {
    q: 'Why does the profile ask about English separately?',
    a: 'Because it changes your step list, not your eligibility. Say you trained in English and the English-evidence step is left out of your journey for Canada, the UK, Australia, and New Zealand. The US route keeps its OET Medicine step for everyone, because ECFMG requires it from everyone, and the step says so.',
  },
];
